<script lang="ts" setup>
import { en, Faker } from '@faker-js/faker'
import { Lock, User } from '@element-plus/icons-vue'
import { computed, reactive, ref, toRef, watch } from 'vue'
// Layout note: this component is rendered inside a parent that sets
// `container-type: inline-size` + `container-name: conn-list`. The single-row
// desktop layout below depends on those — see ConnectionSection.vue.
import Notification from '~/services/notificationService'
import type { ConnectionMetadata } from '~/models/connectionMetadata'
import Help from '~/components/display/Help.vue'
import type {
  GETConnection,
  UserNameAndPasswordConfig,
} from '~/services/apiClient'
import {
  ConnectionsApi,
  ConnectionType,
  ConnectionValidationStatus,
  OrganizationSubscription,
} from '~/services/apiClient'
import { useConnectionsStore } from '~/stores/connections'
import { useUserStore } from '~/stores/user'
import { getUXStatus } from '~/logic/connectionStatus'
import { BRAND_NAME } from '~/constants/branding'
import ConnectionStatusPill from '~/components/credentials/ConnectionStatusPill.vue'

const props = withDefaults(
  defineProps<{
    connectionMeta: ConnectionMetadata
    editable?: boolean
    utasConnectionsOnly?: boolean
    // Only offer ways to get the credential working: no Pause, no Mark Not Used.
    // For the credential prompt, which is about a terminal the user is working with
    // right now, so switching its credential off would just mute the prompt.
    fixOnly?: boolean
  }>(),
  {
    editable: true,
    utasConnectionsOnly: false,
    fixOnly: false,
  }
)

// Deterministic seed from the connection type so each row renders stable — but
// distinct — fake credentials. Stability matters for Chromatic snapshots; a
// fresh random value per mount would diff on every run.
function seedFromConnectionType(value: string): number {
  let hash = 0
  for (let i = 0; i < value.length; i++) {
    hash = (Math.imul(31, hash) + value.charCodeAt(i)) | 0
  }
  return Math.abs(hash)
}

const connectionsStore = useConnectionsStore()
const userStore = useUserStore()
const connectionMeta = toRef(props, 'connectionMeta')

interface CredForm {
  userName: string
  password: string
}

const form = reactive<CredForm>({
  userName: '',
  password: '',
})

const savedCreds = reactive<CredForm>({
  userName: form.userName,
  password: form.password,
})

function updateSavedCreds() {
  savedCreds.userName = form.userName
  savedCreds.password = form.password
}

const existingConnection = ref(null as null | GETConnection)
watch(
  connectionsStore.connectionLookup,
  () => {
    const connection = connectionsStore.connectionLookup.get(
      connectionMeta.value.type
    )
    if (connection) {
      existingConnection.value = connection
      const config = connection.config as UserNameAndPasswordConfig
      // only update if the existing connection has saved credentials
      // otherwise we don't want to overwrite in-progress work
      if (config.user || config.password) {
        form.userName = config.user
        form.password = config.password
        updateSavedCreds()
      }
    }
  },
  { immediate: true }
)

const uxStatus = computed(() => getUXStatus(existingConnection.value))

const hasSomethingEntered = computed(() => form.userName || form.password)

const isDirty = computed(
  () => JSON.stringify(savedCreds) !== JSON.stringify(form)
)

const showSaveButton = computed(
  () =>
    props.editable &&
    hasSomethingEntered.value &&
    (isDirty.value ||
      (existingConnection.value &&
        existingConnection.value.validation_status !==
          ConnectionValidationStatus.Validated &&
        existingConnection.value.validation_status !==
          ConnectionValidationStatus.Disabled))
)

const canMarkNotUsed = computed(
  () =>
    props.editable &&
    !props.fixOnly &&
    !props.utasConnectionsOnly &&
    (uxStatus.value === 'not_set_up' || uxStatus.value === 'failing')
)

const passwordPlaceholder = computed(() => {
  if (props.connectionMeta.type === ConnectionType.ApmTermpointApi) {
    return 'API Key'
  }
  return 'Password'
})

const usernamePlaceholder = computed(() => {
  if (props.connectionMeta.type === ConnectionType.ApmTermpointApi) {
    return 'SCAC'
  }
  return 'Username'
})

const hardCodedUserName = computed((): string | undefined => {
  if (props.connectionMeta.type === ConnectionType.ApmTermpointApi) {
    return userStore.currentOrg?.scac || 'SCAC'
  }
  return undefined
})

// When demoing the product we must never reveal real trucker login info on a
// shared screen. In demo mode we render these fake values read-only instead of
// binding the real `form` to the inputs, so the actual credentials never reach
// the DOM. See `models/trucks.ts` for the same demo-masking pattern.
const demoCreds = computed(() => {
  // A local, freshly-seeded faker keeps this getter pure and never perturbs the
  // global faker sequence other demo-mode models (trucks, containers, …) rely on.
  const faker = new Faker({ locale: [en] })
  faker.seed(seedFromConnectionType(connectionMeta.value.type))
  return {
    userName: faker.internet.username(),
    password: faker.internet.password({ length: 12 }),
  }
})

const validating = ref(false)
const toggling = ref(false)
const markingNotUsed = ref(false)

function saveAndValidate() {
  if (hardCodedUserName.value) {
    form.userName = hardCodedUserName.value
  }
  validating.value = true
  connectionsStore
    .saveAndValidate([
      {
        type: connectionMeta.value.type,
        config: {
          // It's a bummer, our client is merging all fields into one object. We could
          // fix this by making `config_type` a discriminator field in the backend but
          // that would require this field to always be passed which would be backwards
          // incompatible with older clients. So we should/could do this after this
          // code has been out for a while
          // @ts-expect-error - config_type is merged into object by client
          config_type: 'user_name_and_password',
          user: form.userName,
          password: form.password,
        },
      },
    ])
    .then((resp) => {
      if (resp.status === 200) {
        if (
          resp.data[0].validation_status ===
          ConnectionValidationStatus.Validated
        ) {
          Notification.success(
            `Saved and validated ${connectionMeta.value.label} credentials`
          )
        } else if (
          resp.data[0].validation_status === ConnectionValidationStatus.Invalid
        ) {
          Notification.error(
            `Invalid ${connectionMeta.value.label} credentials`
          )
        } else {
          Notification.error(
            `Error validating ${connectionMeta.value.label} credentials`
          )
        }
      }
    })
    .catch((e) => {
      if (e.response.status === 403) {
        const msg = e.response.data.message
        Notification.error(msg)
      } else {
        Notification.error(
          `Error validating ${connectionMeta.value.label} credentials`
        )
      }
    })
    .finally(() => {
      validating.value = false
    })
}

function pauseConnection() {
  toggling.value = true
  const api = new ConnectionsApi()
  api
    .deactivateConnectionsIdDeactivatePut(existingConnection.value!.id)
    .then((resp) => {
      if (resp.status === 200) {
        connectionsStore.load(
          props.utasConnectionsOnly ? OrganizationSubscription.Uas : undefined
        )
        Notification.success(`Paused ${connectionMeta.value.label} connection`)
      } else {
        Notification.error(
          `Error pausing ${connectionMeta.value.label} connection`
        )
      }
    })
    .catch(() => {
      Notification.error(
        `Error pausing ${connectionMeta.value.label} connection`
      )
    })
    .finally(() => {
      toggling.value = false
    })
}

function resumeConnection() {
  toggling.value = true
  const api = new ConnectionsApi()
  api
    .activateConnectionsIdActivatePut(existingConnection.value!.id)
    .then((resp) => {
      if (resp.status === 200) {
        connectionsStore.load(
          props.utasConnectionsOnly ? OrganizationSubscription.Uas : undefined
        )
        Notification.success(`Resumed ${connectionMeta.value.label} connection`)
      } else {
        Notification.error(
          `Error resuming ${connectionMeta.value.label} connection`
        )
      }
    })
    .catch(() => {
      Notification.error(
        `Error resuming ${connectionMeta.value.label} connection`
      )
    })
    .finally(() => {
      toggling.value = false
    })
}

async function markNotUsed() {
  markingNotUsed.value = true
  try {
    await connectionsStore.markNotUsed(connectionMeta.value.type)
  } finally {
    markingNotUsed.value = false
  }
}

function setupConnection() {
  connectionsStore.unmarkNotUsed(connectionMeta.value.type)
}
</script>

<template>
  <div
    class="conn-row"
    :class="{
      'conn-row--failing': !connectionMeta.managed && uxStatus === 'failing',
      'conn-row--not-set-up':
        !connectionMeta.managed && uxStatus === 'not_set_up',
      'conn-row--managed': connectionMeta.managed,
    }"
  >
    <!-- Col 1: portal name + external link + help icon (trailing) -->
    <div class="conn-name">
      <a
        :href="connectionMeta.url"
        target="_blank"
        rel="noreferrer"
        class="conn-portal-link"
      >
        <span class="underline">{{ connectionMeta.label }}</span>
        <i-mdi:open-in-new class="align-text-bottom ml-1 text-xs" />
      </a>
      <!-- A Help Center article beats the inline steps: it stays current and has
           screenshots, so it replaces the popover when the connection has one -->
      <a
        v-if="connectionMeta.helpUrl"
        v-tooltip="`Open the Help Center guide for ${connectionMeta.label}`"
        :href="connectionMeta.helpUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="conn-help"
        data-testid="connection-help"
        :aria-label="`Help Center guide for ${connectionMeta.label}`"
      >
        <i-mdi:help-circle class="align-middle text-lg" />
      </a>
      <Help
        v-else-if="connectionMeta.note"
        class="conn-help"
        data-testid="connection-help"
        :title="connectionMeta.label"
        :markdown-content="connectionMeta.note"
      />
    </div>

    <!-- Col 2: status pill -->
    <ConnectionStatusPill class="conn-pill" :status="uxStatus" />

    <!-- Managed row: read-only, no inputs, no actions. Just an explanatory sub-line. -->
    <template v-if="connectionMeta.managed">
      <div class="conn-middle conn-managed-note">
        Managed by {{ BRAND_NAME }}. No action needed.
      </div>
    </template>

    <!-- Standard editable row: inputs + actions -->
    <template v-else>
      <!-- Col 3 (grows): inputs OR not-used badge -->
      <div v-if="uxStatus === 'not_used'" class="conn-middle conn-not-used">
        Marked as not used. You can set this up when you're ready.
      </div>
      <div v-else class="conn-middle conn-inputs">
        <template v-if="!hardCodedUserName">
          <el-input
            v-if="userStore.demo_mode"
            :model-value="demoCreds.userName"
            :prefix-icon="User"
            :placeholder="usernamePlaceholder"
            readonly
          />
          <el-input
            v-else
            v-model="form.userName"
            :prefix-icon="User"
            :placeholder="usernamePlaceholder"
            autocomplete="off"
            :disabled="!editable"
          />
        </template>
        <el-input
          v-if="userStore.demo_mode"
          :model-value="demoCreds.password"
          :prefix-icon="Lock"
          type="password"
          :placeholder="passwordPlaceholder"
          readonly
        />
        <el-input
          v-else
          v-model="form.password"
          :prefix-icon="Lock"
          type="password"
          :placeholder="passwordPlaceholder"
          show-password
          autocomplete="new-password"
          :disabled="!editable"
        />
      </div>

      <!-- Col 4: action buttons (one or two) -->
      <div class="conn-actions">
        <!-- Primary actions, by status -->
        <el-button
          v-if="uxStatus === 'not_used' && editable"
          size="small"
          type="primary"
          class="conn-btn"
          @click="setupConnection"
        >
          Set Up Now
        </el-button>

        <el-button
          v-else-if="showSaveButton"
          type="success"
          size="small"
          :disabled="validating"
          class="conn-btn"
          @click="saveAndValidate"
        >
          <template v-if="validating">Validating…</template>
          <template v-else>Save and Validate</template>
          <i-mdi:loading v-if="validating" class="conn-btn-spinner" />
        </el-button>

        <el-button
          v-else-if="editable && uxStatus === 'paused'"
          type="primary"
          size="small"
          :disabled="toggling"
          class="conn-btn"
          @click="resumeConnection"
        >
          Resume
          <i-mdi:loading v-if="toggling" class="conn-btn-spinner" />
        </el-button>

        <el-button
          v-if="
            editable && !fixOnly && uxStatus === 'connected' && !showSaveButton
          "
          type="primary"
          size="small"
          :disabled="toggling"
          class="conn-btn"
          @click="pauseConnection"
        >
          Pause
          <i-mdi:loading v-if="toggling" class="conn-btn-spinner" />
        </el-button>

        <el-button
          v-if="canMarkNotUsed"
          type="primary"
          :disabled="connectionsStore.validating || markingNotUsed"
          size="small"
          class="conn-btn"
          @click="markNotUsed"
        >
          Mark Not Used
          <i-mdi:loading v-if="markingNotUsed" class="conn-btn-spinner" />
        </el-button>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
// Mobile default: card stacks vertically inside a per-row bordered card
.conn-row {
  @apply flex flex-col px-4 py-2 gap-2 rounded-lg border border-gray-200 bg-white;
}

.conn-row--failing {
  @apply border-red-300 bg-red-50/40;
}

.conn-row--not-set-up {
  @apply border-orange-300 bg-orange-50/40;
}

.conn-row--managed {
  @apply bg-gray-50/60;
}

// Plain block text with an inline icon — when the text wraps on narrow
// widths the icon stays at the start of the first line, which is the
// expected anchor for a leading-icon sub-line.
.conn-managed-note {
  @apply text-sm text-gray-500;
  line-height: 1.4;
}

.conn-name {
  @apply flex items-center gap-1.5;
}

.conn-help {
  @apply flex-shrink-0;
  line-height: 0; // keep the icon glyph from inheriting text leading
}

.conn-portal-link {
  @apply flex items-center text-sm font-bold text-gray-900 min-w-0;
}

.conn-pill {
  align-self: flex-start;
}

.conn-middle {
  @apply w-full min-w-0;
}

.conn-not-used {
  @apply text-gray-500 text-sm;
}

.conn-inputs {
  @apply flex flex-col gap-2;
}

.conn-actions {
  @apply flex flex-wrap gap-2 w-full;
}

// EP injects `margin-left: 12px` between adjacent el-buttons by default.
// Combined with our flex `gap-2` it gives an oddly large 20px-ish spacing
// between Save & Validate and Mark Not Used. Suppress the EP margin so
// only our gap controls spacing.
.conn-actions :deep(.el-button + .el-button) {
  margin-left: 0;
}

.conn-btn {
  @apply flex-1;
}

// Trailing loading spinner — placed inside el-button slot AFTER the label,
// so it appears on the right edge of the button instead of EP's default
// left-of-text loading indicator.
.conn-btn-spinner {
  @apply ml-1.5;
  animation: conn-spin 0.8s linear infinite;
}

@keyframes conn-spin {
  to {
    transform: rotate(360deg);
  }
}

// ≥640px container: single horizontal row — name | pill | inputs | actions
@container conn-list (min-width: 720px) {
  .conn-row {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }

  .conn-name {
    width: 12rem;
    flex-shrink: 0;
  }

  .conn-pill {
    align-self: center;
    flex-shrink: 0;
  }

  .conn-middle {
    flex: 1;
    width: auto;
  }

  .conn-inputs {
    flex-direction: row;
    gap: 0.5rem;
  }

  .conn-actions {
    width: auto;
    flex-wrap: nowrap;
    flex-shrink: 0;
  }

  .conn-btn {
    flex: 0 0 auto;
    min-width: 7rem;
  }
}
</style>
